import React from 'react';
import { logWarning } from '../../utilities/logging';

/**
 * Size breakpoint configuration for responsive images.
 * @typedef {Object} SizeBreakpoint
 * @property {string} media - Media query condition (e.g., "(max-width: 768px)").
 * @property {string} size - Size value (e.g., "100vw", "50vw", "300px").
 */

/**
 * Generates a srcSet string from the provided sizes and source URL.
 * @param {Object} options - The options object.
 * @param {string} options.src - The image source URL.
 * @param {number[]} options.srcSetSizes - Array of pixel widths for srcSet generation.
 * @returns {string} The generated srcSet string.
 */
const generateSrcSet = ({ src, srcSetSizes }) => {
  return srcSetSizes
    .map((size) => `${src}?width=${size} ${size}w`)
    .join(', ');
};

/**
 * Builds the sizes attribute string with media conditions.
 * @param {Object} options - The options object.
 * @param {SizeBreakpoint[]} options.sizes - Array of media conditions and size values.
 * @param {string} options.fallbackSize - Fallback size when no media conditions match.
 * @returns {string} The generated sizes string.
 */
const buildSizesString = ({ sizes, fallbackSize }) => {
  if (sizes.length === 0) {
    return fallbackSize;
  }

  const mediaConditions = sizes
    .map(({ media, size }) => `${media} ${size}`)
    .join(', ');

  return `${mediaConditions}, ${fallbackSize}`;
};

/**
 * ResponsiveImage component that loads images with proper responsive sizing and lazy loading.
 *
 * The sizes attribute allows you to specify the layout width of the image for each of a list of media conditions.
 * This provides the ability to automatically select among different images as the document state changes.
 *
 * @example
 * <ResponsiveImage
 *   src="https://via.placeholder.com/150"
 *   alt="Placeholder image"
 *   className="my-image"
 *   srcSetSizes={[200, 500, 800, 1200]}
 *   sizes={[{ media: "(max-width: 768px)", size: "100vw" }]}
 *   fallbackSize="100vw"
 * />
 * @param {Object} props - Component props.
 * @param {string} props.src - The image source URL.
 * @param {string} props.alt - The image alternative text.
 * @param {string} [props.className] - Optional CSS class name.
 * @param {number[]} [props.srcSetSizes] - Array of pixel widths for srcSet generation (default: [200, 500, 800, 1200]).
 * @param {SizeBreakpoint[]} [props.sizes] - Array of media conditions and size values for the sizes attribute.
 * @param {string} [props.fallbackSize] - Fallback size when no media conditions match (default: "100vw").
 * @param {boolean} [props.asyncDecoding=true] - Whether to use async decoding.
 * @param {boolean} [props.lazyLoading=true] - Whether to use lazy loading.
 * @param {'async' | 'sync' | 'auto'} [props.decoding] - Image decoding strategy ("async", "sync", or "auto").
 * @param {'lazy' | 'eager'} [props.loading] - Image loading strategy ("lazy", "eager", or "auto").
 * @returns {React.JSX.Element | null} The rendered image element or null if required props are missing.
 */
export const ResponsiveImage = ({
  src,
  alt,
  className,
  srcSetSizes = [200, 500, 800, 1200],
  sizes = [],
  fallbackSize = "100vw",
  decoding = 'async',
  loading = 'lazy'
}) => {
  // Early return if required props are missing.
  if (!src || !alt) {
    logWarning({
      label: 'ResponsiveImage',
      warning: 'Incorrectly configured responsive image.',
      message: 'src and alt props are required.',
      extraData: { src, alt }
    });
    return null;
  }

  // Generate srcSet with proper format: "url width, url 2x".
  const srcSet = generateSrcSet({ src, srcSetSizes });

  // Build sizes attribute string with media conditions.
  const sizesString = buildSizesString({ sizes, fallbackSize });

  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={sizesString}
      alt={alt}
      className={className}
      decoding={decoding}
      loading={loading}
    />
  );
};
